// Countdown: the time left until a moment (data.target, ms since 1970), in large figures, as many groups as its span
// needs: minutes and seconds under an hour, then hours, days, months and years, each with its unit under it, wrapped
// onto rows when the tile is tall enough for that to make them bigger. The span is from
// when it was set (data.start) to its end, so the format holds while it counts down instead of shrinking at every
// boundary; months and years are calendar ones. Edit sets the end from what is typed (a duration like 5m or 1h 30m,
// mm:ss, a time like 3pm, a day like friday 5pm, or a date like July 15 2028) and a caption (data.label); editors
// also get +5s +5m +10m +30m on the face. At zero it says Done, pulses, and a page that was showing it running
// announces it. The kit's heartbeat ticks every countdown on the second; each tile keeps a function (el.tick) that
// only updates the text that changed.
import {
  backgroundItem, currentRole, EDIT, editItem, el$, formDialog, heartbeat, invalid, isLight, lineIcon, render, saveData,
  setText, sizeOf, textWidth, toast,
} from './kit.js';
import { DAY, FORMATS, SEC, holidays, readHoliday, readTarget, remaining, spelled } from './countdown-time.js';

export default {
  kind: 'countdown',
  label: 'Countdown',
  icon: lineIcon('countdown'),
  about: 'A timer counting down to a time or a date',
  look: 'bare',
  sizes: ['2x1', '3x1', '2x2', '3x2'],
  create: () => ({ data: { start: Date.now(), target: Date.now() + 5 * 60 * 1000 } }), // a five-minute timer
  render: renderCountdown,
  fields: '.countdown-add',
  menu: (s) => [
    { label: 'Restart', roles: EDIT, action: () => restart(s) },
    backgroundItem(s),
    editItem(countdownDialog, s, fillCountdownDialog),
  ],
};

const PAD = 10; // around the face (.tile.countdown)
const ADDS = [['+5s', 5e3], ['+5m', 5 * 60e3], ['+10m', 10 * 60e3], ['+30m', 30 * 60e3]];
const target = (s) => Number(s.data?.target) || 0;
const start = (s) => Math.min(Number(s.data?.start) || target(s), target(s));
const formatOf = (s) => FORMATS.find((f) => Math.max(target(s) - start(s), target(s) - Date.now()) < f.below);

const pad = (n) => String(n).padStart(2, '0');
// Each group has its unit under it: without colons, 08 19 04 alone does not say which is which.
const UNIT_LABELS = { y: 'yrs', mo: 'mos', d: 'days', h: 'hrs', m: 'min', s: 'sec' };
// The groups' layout, in em of the figures (must match .countdown-time / .countdown-num / .countdown-unit): the gaps
// between groups across and down, the space between figures and unit, and the unit's size (.2em, at least UNIT_MIN
// px) on a line of 1.25.
const GAP_X = 0.3;
const GAP_Y = 0.12;
const UNIT_SPACE = 0.12;
const UNIT_MIN = 7;
const CAPTION = 20; // the caption's line and the gap above it (.countdown-caption, .tile.countdown)

// The biggest figures that fit the room, and in how many rows: the groups wrap onto as many even rows as make them
// largest (a tall tile stacks them, a wide one keeps one row). Every group is one cell wide, the wider of its two
// figures and its unit, so the columns line up and the gaps stay even.
function fitFigures(units, room) {
  const unit = (size) => Math.max(UNIT_MIN, 0.2 * size);
  const label = Math.max(...units.map((u) => textWidth(UNIT_LABELS[u].toUpperCase(), 100))) / 100 * 1.12; // + spacing
  const digits = textWidth('00', 100) / 100;
  const cell = (size) => Math.max(digits * size, label * unit(size));
  let best = { size: 0, cols: units.length, cell: 0 };
  for (let rows = 1; rows <= units.length; rows++) {
    const cols = Math.ceil(units.length / rows);
    if (rows > 1 && Math.ceil(units.length / (rows - 1)) === cols) continue; // the same columns as fewer rows
    const fits = (size) => cols * cell(size) + (cols - 1) * GAP_X * size <= room.w * 0.96
      && rows * (size * (1 + UNIT_SPACE) + unit(size) * 1.25) + (rows - 1) * GAP_Y * size <= room.h - CAPTION;
    let lo = 0;
    let hi = 96;
    for (let i = 0; i < 20; i++) { const mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; }
    if (lo > best.size) best = { size: lo, cols, cell: cell(lo) };
  }
  const size = Math.max(10, best.size);
  return { size, cols: best.cols, cell: Math.max(digits * size, label * unit(size)) };
}

// When it ends, said briefly: the time today, the weekday and time within a week, else the date (and time unless
// midnight).
function endText(end, now = Date.now()) {
  const d = new Date(end);
  const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(d);
  if (end - now < DAY && d.getDate() === new Date(now).getDate()) return time;
  if (end - now < 6 * DAY) return `${new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(d)} ${time}`;
  const date = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  return d.getHours() || d.getMinutes() ? `${date}, ${time}` : date;
}

function renderCountdown(s, el) {
  el.setAttribute('role', 'timer');
  const label = s.data?.label || '';
  el.setAttribute('aria-label', `Countdown${label ? `, ${label}` : ''}`);
  el.style.setProperty('--countdown-bg', s.data?.color || 'transparent');
  el.classList.toggle('filled', !!s.data?.color);
  el.classList.toggle('light', !!s.data?.color && isLight(s.data.color));
  const { units } = formatOf(s);
  const { w, h } = sizeOf(s);
  const room = { w: w - 2 * PAD, h: h - 2 * PAD };
  const fit = fitFigures(units, room);
  const figures = el$('div', 'countdown-time');
  // the cell width and how many fit across, so the groups wrap onto even rows (3 + 3, never 5 + 1)
  figures.style.fontSize = `calc(${fit.size.toFixed(1)}px * var(--s))`;
  figures.style.setProperty('--cell', `calc(${fit.cell.toFixed(1)}px * var(--s))`);
  figures.style.maxWidth = `calc(${(fit.cols * fit.cell + (fit.cols - 1) * GAP_X * fit.size + 1).toFixed(1)}px * var(--s))`;
  const nums = units.map((u) => {
    const group = el$('span', 'countdown-group');
    const num = el$('span', 'countdown-num');
    group.append(num, el$('span', 'countdown-unit', UNIT_LABELS[u]));
    figures.append(group);
    return num;
  });
  const caption = el$('div', 'countdown-caption');
  const parts = [figures, caption];
  if (currentRole() === 'editor') parts.push(addButtons(s, el)); // viewers of a locked webtop change nothing
  el.replaceChildren(...parts);
  let wasRunning = null; // whether the last tick showed it running, so only a countdown seen running announces its end
  el.tick = () => {
    const now = Date.now();
    const end = target(s);
    const { secs, parts: left } = remaining(end, units, now);
    left.forEach((n, i) => setText(nums[i], pad(n)));
    const done = secs === 0;
    if (el.classList.contains('done') !== done) el.classList.toggle('done', done);
    setText(caption, done ? (label ? `${label} · Done` : 'Done') : (label || endText(end, now)));
    const title = done ? `Ended ${new Date(end).toLocaleString()}`
      : `${spelled(units, left) || '0 seconds'} left, until ${new Date(end).toLocaleString()}`;
    if (el.title !== title) el.title = title;
    if (done && wasRunning) toast(`${label || 'Countdown'}: time's up`);
    wasRunning = !done;
  };
  el.tick();
  stopTicks ??= heartbeat(tickCountdowns);
}

// +5s +5m +10m +30m: more time on the clock. A countdown that has ended starts again from now, with the time added.
function addButtons(s, el) {
  const row = el$('div', 'countdown-add');
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', 'Add time');
  for (const [text, ms] of ADDS) {
    const b = el$('button', '', text);
    b.type = 'button';
    b.title = `Add ${text.slice(1).replace('s', ' seconds').replace('m', ' minutes')}`;
    b.addEventListener('click', async (e) => {
      e.stopPropagation();
      const now = Date.now();
      const ended = target(s) <= now;
      const patch = { target: (ended ? now : target(s)) + ms };
      if (ended) patch.start = now;
      const before = formatOf(s);
      await saveData(s, patch);
      if (formatOf(s) !== before) renderCountdown(s, el); else el.tick(); // a longer span may need more groups
    });
    row.append(b);
  }
  return row;
}

// Restart: the same length again, from now.
async function restart(s) {
  const now = Date.now();
  const length = Math.max(SEC, target(s) - start(s));
  await saveData(s, { start: now, target: now + length });
  render();
}

// Every countdown on the webtop ticks on the heartbeat, subscribed while there is one (renderCountdown subscribes
// again).
let stopTicks = null;
function tickCountdowns() {
  const countdowns = document.querySelectorAll('#webtop .tile.countdown');
  if (!countdowns.length) { stopTicks(); stopTicks = null; return; }
  countdowns.forEach((el) => el.tick?.());
}

// Edit: when it ends (read as it is typed, with a preview under the field) and the caption. An empty field keeps the
// end as it is.
const countdownDialog = formDialog('countdown', {
  title: 'Countdown',
  fields: `
    <label>Count down to
      <input type="text" name="until" maxlength="80" placeholder="3s/m/h/d, mm:ss, 3am" autocapitalize="off" spellcheck="false"
        list="countdown-holidays">
    </label>
    <datalist id="countdown-holidays"></datalist>
    <p class="countdown-preview muted" aria-live="polite"></p>
    <label>Caption
      <input type="text" name="label" maxlength="40" placeholder="Pizza">
    </label>`,
  check(f) {
    const label = f.label.value.trim().slice(0, 40);
    if (!f.until.value.trim()) return { data: { label } };
    const now = Date.now();
    const end = readTarget(f.until.value, now) ?? invalid('Try 5m, 1h 30m, 05:00, 3pm, friday 5pm or July 15 2028.');
    if (end <= now) invalid('That moment has passed. Pick one in the future.');
    if (end > now + 200 * 365 * DAY) invalid('That is too far away.');
    return { data: { target: end, start: now, label } };
  },
  setup(form) {
    form.elements.until.addEventListener('input', () => { showPreview(form); nameHoliday(form); });
  },
});
let editing = null; // the countdown the dialog is open for, whose end the preview shows while the field is empty
let named = ''; // the caption nameHoliday filled in, so it can take it out again
// A holiday typed in names the countdown: its name replaces the caption, whatever was there. When the field stops
// reading as a holiday, a caption it filled comes out again; one the user wrote since stays.
function nameHoliday(form) {
  const caption = form.elements.label;
  const name = readHoliday(form.elements.until.value)?.name;
  if (name) caption.value = name;
  else if (named && caption.value === named) caption.value = '';
  named = name ?? '';
}
// The holidays as suggestions for the field, the coming ones first, each with its date (filled on every opening, so
// the order and dates stay current). Picking one types its name, which reads as that holiday.
function suggestHolidays(form) {
  const date = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  form.querySelector('#countdown-holidays').replaceChildren(...holidays().map(({ name, end }) => {
    const option = document.createElement('option');
    option.value = name;
    option.label = date.format(end);
    return option;
  }));
}
function showPreview(form, s = editing) {
  const p = form.querySelector('.countdown-preview');
  const typed = form.elements.until.value;
  const now = Date.now();
  if (!typed.trim()) {
    p.textContent = s ? `Ends ${new Date(target(s)).toLocaleString()} (leave empty to keep)` : '';
    p.classList.remove('bad');
    return;
  }
  const end = readTarget(typed, now);
  const bad = !end || end <= now;
  p.classList.toggle('bad', bad);
  if (bad) { p.textContent = end ? 'That moment has passed.' : 'Not read yet: try 5m, 05:00, 3pm or July 15 2028.'; return; }
  const { units } = FORMATS.find((x) => end - now < x.below);
  const left = remaining(end, units, now).parts;
  p.textContent = `Ends ${new Date(end).toLocaleString()} · in ${spelled(units, left) || 'a moment'}`;
}
function fillCountdownDialog(f, s) {
  editing = s;
  named = '';
  suggestHolidays(f.until.form);
  f.until.value = '';
  f.label.value = s.data?.label ?? '';
  showPreview(f.until.form, s);
}
